
<div class="column-filter">
  <div v-if="loading" class="d-flex justify-content-center align-items-center py-4 h-100">
    <v-progress-circular color="primary" indeterminate></v-progress-circular>
  </div>
  <div v-else>
    <span class="font-weight-bold">Filter</span>
    <v-divider color="#D7D7D7" class="mt-2"></v-divider>
    <div v-if="exportSelect === 'collection' || selectedExportReport.type === 'Report::Ticket'"
      style="background-color: #FFF2E0; border-radius: 4px;" class="mb-2 px-2 py-1">
      <span>Sample data remains unchanged because calculations are required.</span>
    </div>

    <div v-if="filteredFilters.length">
      <div v-for="filter in filteredFilters" :key="filter.name" class="mb-3">
        <label class="form-label">{{ getFilterLabel(filter) }}</label>

        <DynamicDateFilter v-if="filter.data_type === 'date'" :filter="filter"
          :radio-value="getDateFilterRadioValue(filter)"
          :presets="getDynamicPresetsForFilter(filter)"
          :dynamic-value="getDynamicFilterValue(filter)"
          :has-custom-config="Boolean(dynamicDateCustomConfig[filter.keys[0]])"
          :custom-value="getCustomDynamicValue(filter)"
          :custom-unit="getCustomDynamicUnit(filter)"
          :custom-max="getCustomDynamicMax(filter)"
          :unit-options="dynamicDateUnitOptions"
          :start-value="filterValues[filter.keys[0]]"
          :end-value="filter.keys[1] ? filterValues[filter.keys[1]] : ''"
          dynamic-description="Calculated from today's date for preview."
          note-text="Dynamic date filters are calculated from today's date for preview purposes."
          @radio-change="setDateFilterRadioValue"
          @preset-select="selectDynamicPreset"
          @custom-value-input="setCustomDynamicValue"
          @custom-unit-input="setCustomDynamicUnit"
          @start-date-input="$set(filterValues, filter.keys[0], $event)"
          @end-date-input="filter.keys[1] && $set(filterValues, filter.keys[1], $event)" />

        <!-- Multi-Checkbox -->
        <div v-else-if="filter.data_type === 'checkbox'">
          <!-- Grouped View (when parent has multiple values) -->
          <div v-if="Array.isArray(filter.data) && isGroupedFilter(filter)">
            <!-- Select All for grouped departments -->
            <div class="custom-control custom-checkbox mb-2">
              <input type="checkbox" :id="`${filter.keys[0]}-select-all`" :checked="isAllDepartmentsSelected(filter)"
                @change="toggleAllDepartments(filter, $event.target.checked)" class="custom-control-input">
              <label :for="`${filter.keys[0]}-select-all`" class="custom-control-label font-weight-bold">
                All
              </label>
            </div>
            <div v-for="(group, groupId) in getGroupedData(filter)" :key="groupId" class="mb-3"
              v-if="group.items && group.items.length > 0">
              <div class="parent-group-header"><b>{{ group.parentName }}</b></div>
              <div v-for="option in group.items" :key="option.id || option.name"
                class="custom-control custom-checkbox ml-2">
                <input type="checkbox" :id="`${filter.keys[0]}-${option.id}`" :value="option.id"
                  v-model="filterValues[filter.keys[0]]" class="custom-control-input"
                  @change="handleCheckboxChange(filter.keys[0], option.id)">
                <label :for="`${filter.keys[0]}-${option.id}`" class="custom-control-label">
                  {{ option.name || '-' }}
                </label>
              </div>
            </div>
          </div>
          <!-- Flat View (default) -->
          <div v-else-if="Array.isArray(filter.data)">
            <!-- Select All for Business Unit -->
            <div v-if="filter.keys[0] === businessUnitKey" class="custom-control custom-checkbox mb-2">
              <input type="checkbox" :id="`${filter.keys[0]}-select-all`"
                :checked="isAllBusinessUnitsSelected(filter)"
                @change="toggleAllBusinessUnits(filter, $event.target.checked)" class="custom-control-input">
              <label :for="`${filter.keys[0]}-select-all`" class="custom-control-label font-weight-bold">
                All
              </label>
            </div>
            <!-- Select All for Department (when 1 BU selected - flat view) -->
            <div v-else-if="filter.keys[0] === departmentKey" class="custom-control custom-checkbox mb-2">
              <input type="checkbox" :id="`${filter.keys[0]}-select-all`"
                :checked="isAllDepartmentsSelectedFlat(filter)"
                @change="toggleAllDepartmentsFlat(filter, $event.target.checked)" class="custom-control-input">
              <label :for="`${filter.keys[0]}-select-all`" class="custom-control-label font-weight-bold">
                All
              </label>
            </div>
            <div v-for="option in filteredData(filter)" :key="option.id || option.name"
              class="custom-control custom-checkbox"
              v-if="!(filter.keys[0] === businessUnitKey && (option.id === 'all' || option.id === 'any'))">
              <input type="checkbox" :id="`${filter.keys[0]}-${option.id}`" :value="option.id"
                v-model="filterValues[filter.keys[0]]" class="custom-control-input"
                @change="handleCheckboxChange(filter.keys[0], option.id)">
              <label v-if="filter.keys[0] === 'sticker_label_ids'" :for="`${filter.keys[0]}-${option.id}`"
                class="custom-control-label sticker" :style="{ backgroundColor: option.color }">
                {{ option.name || '-' }}
              </label>
              <label v-else-if="filter.keys[0] === 'template_ids' && option.id !== 'all'"
                :for="`${filter.keys[0]}-${option.id}`" class="custom-control-label"
                :style="{ backgroundColor: option.color }">
                #{{ option.id }} {{ option.name || '-' }}
              </label>
              <label v-else :for="`${filter.keys[0]}-${option.id}`" class="custom-control-label">
                {{ option.name || '-' }}
              </label>
            </div>
          </div>
          <v-autocomplete v-else v-model="filterValues[filter.keys[0]]" :items="dynamicOptions[filter.keys[0]]"
            :search-input.sync="searchInputs[filter.keys[0]]" :attach="true" item-value="id" item-text="name"
            label="Search" append-icon="mdi-magnify" solo dense flat hide-details hide-no-data multiple small-chips
            deletable-chips cache-items @update:search-input="fetchFilterData(filter.keys[0])"
            @change="searchInputs[filter.keys[0]] = ''"></v-autocomplete>
        </div>

        <!-- Select Dropdown -->
        <div v-else-if="filter.data_type === 'select'" class="position-relative">
          <v-icon class="search-icon position-absolute" style="top: 5px; right: 10px;">mdi-menu-down</v-icon>
          <select v-model="filterValues[filter.keys[0]]" class="form-control">
            <option v-for="option in filter.data" :key="option.id ? option.id : option"
              :value="option.id ? option.id : option" class="text-capitalize">
              {{ option.name_code_format ? option.name_code_format : option.name ? option.name : option }}
            </option>
          </select>
        </div>

        <!-- Radio Button Option -->
        <div v-else-if="filter.data_type === 'radio_button'">
          <div v-for="option in filter.data" :key="option.id ? option.id : option"
            class="custom-control custom-radio">
            <input type="radio" :id="`${filter.keys[0]}-${option.id ? option.id : option}`"
              :value="option.id ? option.id : option" v-model="filterValues[filter.keys[0]]"
              class="custom-control-input">
            <label :for="`${filter.keys[0]}-${option.id ? option.id : option}`" class="custom-control-label">
              {{ option.name ? option.name : option }}
            </label>
          </div>
        </div>
        <!-- Radio -->
        <div v-else-if="filter.data_type === 'radio'">
          <div v-for="option in filteredData(filter)"
            @click="handleRadioChecked(filter.keys[0], option.id ? option.id : option)"
            :key="option.id ? option.id : option" class="px-4 py-1 hover-light-blue" style="margin: 0 -16px 0 -16px;"
            :class="{
              'bg-blue-active': filterValues[filter.keys[0]] === (option.id ? option.id : option),
            }">
            <input type="radio" :id="`${filter.keys[0]}-${option.id}`" :value="option.id"
              v-model="filterValues[filter.keys[0]]" style="opacity:1"
              @click="handleRadioChecked(filter.keys[0], option.id ? option.id : option)">
            {{ option.name ? option.name : option }}
          </div>

          <!-- custom start date -->
          <div v-if="filterValues[filter.keys[0]] == 'custom'" class="d-flex" style="gap: 16px;">
            <div>
              <label class="form-label mb-1" style="font-size: 12px;">Start Date</label>
              <CInput v-model="customStartDate" type="date" placeholder="mm/dd/yyyy"
                style="width: 142px !important;" />
            </div>
            <div>
              <label class="form-label mb-1" style="font-size: 12px;">End Date</label>
              <CInput v-model="customEndDate" type="date" placeholder="mm/dd/yyyy" style="width: 142px !important;" />
            </div>
          </div>
        </div>

        <!-- Default Text Input -->
        <input v-else v-model="filterValues[filter.keys[0]]" type="text" class="form-control">
      </div>
    </div>

    <div class="d-flex action justify-content-between py-2 px-6 position-absolute"
      style="bottom: 0; right: 0; width: 100%;">
      <button class="text-primary" @click="clearFilters">Clear</button>
      <CButton :color="'#007BFF'" :disabled="disabledApplyFilter" label="Apply" @click.native="applyFilters">
      </CButton>
    </div>
  </div>
</div>
